import { useState } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import dayjs from "dayjs";

import { InboundLayout } from "ui-components/Layout";
import {
  InboundSubmitButton,
  InboundDetailEditButton,
} from "ui-components/Form";

import ASN from "./components/ASN";
import { headerRender, headerValues, headerValidation } from "./asn-schema";
import { detailRender, detailValues, detailValidation } from "./asn-details-schema";
import { useCustomerValue } from "providers/CustomerContext";
import ASNDetail from "./components/ASNDetail";
import { Success, Error_ } from "ui-components/Alert";
import { InfoDialog } from "ui-components/Alert";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createASN } from "services/data.inbound.service";
import { asnColumns } from "./columns";
import { getPOOptions } from "services/data.inbound.service";
import BasicTable2 from "ui-components/tables/BasicTable2";
import { getOpenPO } from "services/data.inbound.service";
import { LoadingDialog } from "ui-components/Alert";
import { InboundHeaderCard } from "ui-components/Layout";
import { ASNCaption } from "../components/Caption";
import { deleteDetail, getTotalQuantity, updateDetail } from "../common/common";
import { statusLabel } from "layouts/views/common/enum";
import { useNavigate } from "react-router-dom";
import { useVendorOptions } from "hooks/useQueryHook";

const defaultValues = {
  ...headerValues,
  dateExpected: dayjs(new Date()).format("YYYY-MM-DD"),
  status: "Open",
};
function AddASN() {
  const navigate = useNavigate();
  const { customerCode } = useCustomerValue();
  const [vendorOptions, setVendorOptions] = useState([]);
  const formValues = { ...defaultValues, customerCode };
  const [alert, setAlert] = useState(null);
  const [details, setDetails] = useState([]);
  const [poOptions, setPOOptions] = useState([]);
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);

  const createASNMutation = useMutation(createASN, {});

  const form = useForm({
    defaultValues: formValues,
    values: formValues,
    resolver: yupResolver(headerValidation),
  });
  const {
    control,
    watch,
    formState: { errors, isSubmitting },
    setValue,
    getValues,
    trigger,
  } = form;
  useVendorOptions({
    customerCode,
    onSuccess: (data) => {
      const vendors = data.data.data;

      setVendorOptions(vendors);
      if (vendors.length > 0) setValue("vendorCode", vendors[0]);
    },
  });

  const headerProps = { headerRender, poOptions, vendorOptions, control, setValue, errors };

  const [poNumber, vendorCode] = watch(["poNumber", "vendorCode"]);
  useQuery({
    queryKey: ["po-options", { customerCode, vendorCode }],
    queryFn: () => getPOOptions({ customerCode, vendorCode }),
    enabled: Boolean(customerCode) && Boolean(vendorCode),
    onSuccess: (data) => setPOOptions(data.data.data),
    onError,
  });
  const { isFetching } = useQuery({
    queryKey: ["po", { customerCode, poNumber }],
    queryFn: () => getOpenPO({ poNumber, customerCode }),
    enabled: Boolean(poNumber) && Boolean(customerCode),
    onSuccess: (data) => {
      const {
        sellerCompany,
        sellerAddress1,
        sellerAddress2,
        sellerPostcode,
        sellerCity,
        sellerState,
        sellerCountry,
        sellerPhoneNo,
        sellerFaxNo,
        remark,
        poDetails,
      } = data.data.data;
      setValue("sellerCompany", sellerCompany);
      setValue("sellerAddress1", sellerAddress1);
      setValue("sellerAddress2", sellerAddress2);
      setValue("sellerPostcode", sellerPostcode);
      setValue("sellerCity", sellerCity);
      setValue("sellerState", sellerState);
      setValue("sellerCountry", sellerCountry);
      setValue("sellerPhone", sellerPhoneNo);
      setValue("sellerFaxNo", sellerFaxNo);
      setValue("remark", remark);
      setDetails(poDetails.map((detail) => ({ ...detail, status: statusLabel[detail.status] })));
    },
    onError,
  });
  const detailProps = { details, setDetails };

  const onSubmit = async () => {
    window.scrollTo(0, 0);
    const isValid = await trigger();
    if (!isValid) return;

    const data = getValues();

    if (details.length === 0) {
      setAlert(<Error_ content="Invalid ASN Details" onChange={setAlert} />);
      return;
    }

    const req = {
      ...data,
      asnDetails: details.map((detail) => {
        const { lineNo, status, ...detail_data } = detail;
        return { ...detail_data, status: "O" };
      }),
    };

    createASNMutation.mutate(req, {
      onSuccess: (data) => navigate("/inbound/asn", { state: data.data.message }),
      onError,
    });
  };
  return (
    <InboundLayout lg={12}>
      {isFetching && <LoadingDialog />}
      {alert}
      <InboundHeaderCard>
        <form noValidate>
          <ASN {...headerProps} />
        </form>
      </InboundHeaderCard>

      <Detail {...detailProps} />
      <InboundSubmitButton label="Submit" onClick={onSubmit} disabled={isSubmitting} />
    </InboundLayout>
  );
}

const Detail = ({ details, setDetails }) => {
  const columns = [
    {
      field: "actions",
      headerName: "Action",
      renderActions: ["Delete"],
      type: "actions",
    },
    ...asnColumns,
  ];
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [formValues, setFormValues] = useState(null);
  const lineNo = formValues ? formValues.lineNo : details.length + 1;

  const form = useForm({
    defaultValues: { ...detailValues, lineNo },
    values: formValues || { ...detailValues, lineNo },
    resolver: yupResolver(detailValidation),
  });
  const {
    control,
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
  } = form;

  const detailProps = { detailRender, control, errors, getValues };

  const onSubmit = (data) => {
    setDetails(updateDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Updated" onChange={setAlert} />);
  };

  const onRowClick = (values) => {
    if (formValues !== null && isDirty) {
      setDialog(
        <InfoDialog
          title={`Unsaved changes for Line No ${formValues.lineNo}`}
          content="Do you want to save the changes"
          onOk={() => {
            const data = getValues();
            setDetails(updateDetail(details, data));
            setDialog(null);
            setFormValues(values);
          }}
          onClose={() => {
            setDialog(null);
            setFormValues(values);
          }}
        />
      );
    } else {
      setFormValues(values);
    }
    return true;
  };

  const onDelete = (data) => {
    setDetails(deleteDetail(details, data));
    setFormValues(null);
    setAlert(<Success content="Deleted" onChange={setAlert} />);
  };

  const totalQuantity = getTotalQuantity(details, "qtyExpected");
  const caption = <ASNCaption totalRows={details.length} totalQuantity={totalQuantity} />;

  const tableProps = { columns, caption, rows: details, onRowClick, onDelete };

  return (
    <InboundHeaderCard>
      {alert}
      {dialog}
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <ASNDetail {...detailProps} />
        {formValues !== null && (
          <InboundDetailEditButton disabled={!isDirty} onClick={() => setFormValues(null)} />
        )}
      </form>

      <BasicTable2 {...tableProps} />
    </InboundHeaderCard>
  );
};

export default AddASN;
